import {
  type Mutation,
  MutationCache,
  type Query,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query';
import { ApiErrorCode, isApiError } from './apiError';

/**
 * Мета-поле запросов/мутаций: errorToast: false — ошибку показывает сам экран
 * (например, форма входа), глобальный тост не нужен.
 */
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { errorToast?: boolean };
    mutationMeta: { errorToast?: boolean };
  }
}

export interface QueryErrorHandlers {
  /** Глобальное уведомление об ошибке (тост с локализованным сообщением). */
  notify: (error: unknown) => void;
}

/** 4xx не повторяем (кроме 408/429 — их тоже не повторяем автоматически: ждём действия пользователя). */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

/** Ошибки, которые не показываются глобальным тостом. */
function isSilent(error: unknown): boolean {
  if (!isApiError(error)) return false;
  // 401 — переход на вход; 422 — ошибки показывает форма (поля и плашка); отмена — не ошибка.
  return (
    error.code === ApiErrorCode.Unauthorized ||
    error.code === ApiErrorCode.Aborted ||
    error.status === 422
  );
}

export function createQueryClient(handlers?: QueryErrorHandlers): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
    queryCache: new QueryCache({
      onError: (error: unknown, query: Query<unknown, unknown, unknown>) => {
        // Первичная загрузка показывает ошибку на экране (PageState); тост — только если
        // уже показанные данные не удалось обновить.
        if (!handlers || isSilent(error) || query.meta?.errorToast === false) return;
        if (query.state.data !== undefined) handlers.notify(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (
        error: unknown,
        _variables: unknown,
        _context: unknown,
        mutation: Mutation<unknown, unknown, unknown, unknown>,
      ) => {
        if (!handlers || isSilent(error) || mutation.meta?.errorToast === false) return;
        handlers.notify(error);
      },
    }),
  });
}
